import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function GrantHarroldRoyalButlerMemoirPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Grant Harrold Royal Butler Memoir Published' }
  ];

  const article = {
    category: 'Cultural Authority & Media',
    date: 'August 28, 2025',
    title: 'Grant Harrold Royal Butler Memoir Published',
    subtitle: 'Former Butler to King Charles Shares Seven Years at the Palace',
    heroImage: '/images/generated/jba-fs-081-news_grant-harrold-royal-butler-memoir.webp',
    lead: 'Grant Harrold, former butler to King Charles (then Prince of Wales), has published a memoir "The Royal Butler: My Remarkable Life in Royal Service" chronicling his seven years at the palace. His journey from working class to royal butler and experiences at the pinnacle of the profession are attracting global attention.',
    sections: [
      {
        heading: 'Book Overview and Author Background',
        image: '/images/generated/jba-fs-082-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: 'A memoir chronicling years of royal service',
        paragraphs: [
          '"The Royal Butler: My Remarkable Life in Royal Service" was published on August 28, 2025 by Orion Publishing (UK edition) and Pegasus Books (US edition).',
          'Author Grant Harrold (born April 26, 1978) is from a working-class family in Airdrie, Scotland, and grew up fascinated by the British Royal Family, dreaming of working for them. He is now widely known as Britain\'s "foremost authority on etiquette."'
        ]
      },
      {
        heading: 'Royal Career and Duties',
        image: '/images/generated/jba-fs-083-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: 'Highgrove House, the royal residence in the Cotswolds',
        paragraphs: [
          'Harrold served as butler to Prince Charles (now King Charles III) for seven years from 2004 to 2011. His primary workplace was Highgrove House, the royal residence in the Cotswolds, where he served King Charles III and Queen Camilla, Prince William and Princess Catherine, and Prince Harry.',
          'On special occasions, he had the honor of serving as butler to Queen Elizabeth II and Prince Philip, gaining the highest level of experience as a royal butler.'
        ]
      },
      {
        heading: 'Memoir Content and Unique Perspective',
        image: '/images/generated/jba-fs-084-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: 'The daily life of the palace as seen from behind the scenes',
        paragraphs: [
          'The book reveals previously untold stories about the unique relationship between the Royal Family and their staff. It details how a working-class boy reached the heart of the Royal Family, daily life at Highgrove House, interactions with royal members, and friendships and teamwork with fellow staff.',
          'It also candidly discusses close encounters with celebrities and world leaders, and perks such as first-class travel and fine dining, presenting the reality of being a royal butler without reservation.'
        ]
      },
      {
        heading: 'Social Impact and Current Activities',
        image: '/images/generated/jba-fs-085-news_grant-harrold-royal-butler-memoir-v3.webp',
        alt: 'A wide social media following built after publication',
        paragraphs: [
          "Following the book's publication, Grant Harrold has approximately 1 million social media followers, recording 3-9 million monthly interactions across Instagram, TikTok, YouTube, and Facebook.",
          'He currently operates under "The Royal Butler" brand, running an etiquette school. Through educational business leveraging his royal experience, he continues activities to widely communicate the value and importance of butler services to society.'
        ]
      },
      {
        heading: 'Critical Reception and Notable Topics',
        image: '/images/generated/jba-fs-086-news_grant-harrold-royal-butler-memoir-v4.webp',
        alt: 'Critical acclaim and reviews of the memoir',
        paragraphs: [
          'Kirkus Reviews praised the book as "an enjoyable memoir of a royal servant," commenting that "service in aristocratic households demands as much drudgery as skill, but he remains starry-eyed throughout, recounting friendships and camaraderie with fellow workers, occasional pranks, and perks."',
          'Notably, Harrold states in the book that his memories differ greatly from Prince Harry\'s portrayal of the Royal Family in his memoir "Spare." While Harrold remembers them as a "happy family," Prince Harry depicts struggles, commenting "it\'s as if we were in two different households."'
        ]
      }
    ],
    comment: "The publication of Grant Harrold's memoir is a groundbreaking event of cultural significance as the experience of a royal butler—the pinnacle of the profession—attracts global attention, and our association deeply respects this achievement. The journey from working class to royal butler demonstrates the importance of professionalism and significantly contributes to improving social recognition and respect for the butler profession. The universal value of British-style butler service traditions being communicated to the world in this form is extremely important for the development of butler culture. As an indication of new recognition expansion possibilities for the butler profession in the social media age, it is rich in suggestions for improving butler service recognition in Japan, and we will make even greater efforts toward developing butlers with such cultural influence and improving their social status.",
    source: [
      { label: 'Source', value: 'Amazon, Simon & Schuster, Wikipedia', link: false },
      { label: 'Article URL', value: 'https://www.simonandschuster.com/books/The-Royal-Butler/Grant-Harrold/9798897100156', link: true },
      { label: 'Access Date', value: 'October 2025', link: false }
    ],
    related: [
      { to: '/en/news/downton-abbey-grand-finale', image: '/images/generated/jba-fs-en-012-news_grant-harrold-royal-butler-memo.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Downton Abbey: The Grand Finale Film Release', title: 'Downton Abbey: The Grand Finale Film Release', desc: "Legendary butler Mr. Carson's return" },
      { to: '/en/news/raffles-london-campaign', image: '/images/generated/jba-fs-en-013-news_grant-harrold-royal-butler-memo.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles "The Butler Did It" Campaign Chapter 2', title: 'Raffles "The Butler Did It" Campaign Chapter 2', desc: 'Luxury hotel butler service innovation' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: 'Source', value: 'Amazon, Simon & Schuster, Wikipedia', link: false },
    { label: 'Official Book Page', value: 'https://www.simonandschuster.com/books/The-Royal-Butler/Grant-Harrold/9798897100156', link: true },
    { label: 'Author Biography', value: 'https://en.wikipedia.org/wiki/Grant_Harrold', link: true },
    { label: 'Interview', value: 'https://www.marieclaire.com/celebrity/royals/royal-butler-grant-harrold-interview/', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];
  const toc = [
    { id: 's-overview', label: 'Book Overview and Author Background' },
    { id: 's-bookinfo', label: "The Book's Publication Details" },
    { id: 's-career', label: 'Royal Career and Duties' },
    { id: 's-content', label: 'Memoir Content and Unique Perspective' },
    { id: 's-impact', label: 'Social Impact and Current Activities' },
    { id: 's-anecdotes', label: 'Reported Royal Anecdotes and Current Work' },
    { id: 's-reviews', label: 'Critical Reception and Notable Topics' }
  ];

  return (
    <Layout>
      <title>Grant Harrold Royal Butler Memoir Published | Japan Butler Association</title>
      <meta name="description" content="Grant Harrold, former butler to King Charles, publishes a memoir chronicling seven years at the palace, sharing his journey from working class to the pinnacle of the butler profession." />
      <link rel="canonical" href="https://butler-association.jp/en/news/grant-harrold-royal-butler-memoir" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/grant-harrold-royal-butler-memoir" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/grant-harrold-royal-butler-memoir" />

      <ArticleHero eyebrow="News / Culture" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Grant Harrold, former butler to King Charles (then Prince of Wales), published a memoir on his seven years at the palace',
                'He served at Highgrove House from 2004 to 2011, also serving the Queen and Prince William and Princess Catherine',
                'He now runs etiquette education as "The Royal Butler," with about 1 million social media followers'
              ]}
            />

            <SectionHeading id="s-overview" en="Overview">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-bookinfo" en="Book Details">The Book's Publication Details</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The US edition is published by Pegasus Books (distributed by Simon & Schuster), released on <Stat>September 23, 2025</Stat>. It is a hardcover of <Stat>272 pages</Stat>, ISBN 9798897100156, with a list price of $29.95 (some sources cite a UK Orion Publishing edition dated August 28, 2025).</p>
              <p className={P}>The memoir traces his path from a childhood fascination with the monarchy to butler life at Highgrove House, recounting the relationship between the royals and their staff, and matters of etiquette, as "untold stories."</p>
            </div>
            <SourceShot
              src="/images/news-sources/harrold-simonschuster.jpg"
              alt="Screenshot of the Simon & Schuster official page listing the bibliographic details of the memoir The Royal Butler"
              media="Simon & Schuster (official)"
              url="https://www.simonandschuster.com/books/The-Royal-Butler/Grant-Harrold/9798897100156"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="It's as if we were in two different households." cite="Grant Harrold" />
            </div>

            <SectionHeading id="s-career" en="Royal Career">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-content" en="The Memoir">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-impact" en="Social Impact">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-anecdotes" en="Royal Anecdotes">Reported Royal Anecdotes and Current Work</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Reported anecdotes include dancing with Queen Elizabeth II at the Balmoral Ghillies Ball in 2005 and being pelted with a water balloon by Prince Harry, candidly portraying staff camaraderie and an informal side of royal life.</p>
              <p className={P}>Harrold left Highgrove House in May 2011 (his role made redundant) and now works as "The Royal Butler," an etiquette expert, media commentator and speaker, appearing aboard cruise lines including Cunard, Princess Cruises and Seabourn.</p>
            </div>
            <SourceShot
              src="/images/news-sources/harrold-wikipedia.jpg"
              alt="Screenshot of the English Wikipedia page describing Grant Harrold's career"
              media="Wikipedia (Grant Harrold)"
              url="https://en.wikipedia.org/wiki/Grant_Harrold"
            />

            <SectionHeading id="s-reviews" en="Reception">{S[4].heading}</SectionHeading>
            <div className="space-y-6">{S[4].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Publisher</dt><dd className="text-[#333] mt-0.5">Pegasus Books</dd></div>
                  <div><dt className="text-[#8f7a56]">Release</dt><dd className="text-[#333] mt-0.5">September 23, 2025 (US)</dd></div>
                  <div><dt className="text-[#8f7a56]">Format</dt><dd className="text-[#333] mt-0.5">Hardcover, 272 pp</dd></div>
                  <div><dt className="text-[#8f7a56]">Price</dt><dd className="text-[#333] mt-0.5">$29.95</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
